import {
  COMMERCE_CATEGORY,
  EVENT_MEDIA_PUBLISH_STATUS,
  EVENT_MEDIA_TYPE,
  EVENT_STATUS,
  EVENT_TYPE,
  ORDER,
  SORT,
} from "~/constants";
import EventsAPI from "~/services/events";
import SubscriptionAPI from "~/services/subscription";
import useUserStore from "~/stores/user";

const useEventStore = defineStore("event", {
  state: () => ({
    data: null as null | IModelEvent,
    commerceOptions: [] as IModelCommerce[],
    isEventStartingSoon: false,
    loading: false,
    clips: {
      isEditingClip: false,
      isCreatingClip: false,
    },
    channelProgram: {} as IModelLinearProgram,
    canReloadSuggested: false,
    userClips: [] as IModelMediaEvent[],
    userHighlights: [] as IModelMediaEvent[],
    gameHighlights: [] as IModelMediaEvent[],
    playerHighlights: [] as IModelMediaEvent[],
    scoreStats: null as null | IEventScoreStats,
    gameStats: null as null | IEventGameStats,
    athleteStats: null as null | IEventAthleteStats,
  }),

  getters: {
    activePPVTicket: (state) => {
      const userStore = useUserStore();

      return userStore.ppvTickets.find(t => state.commerceOptions.find(c => t.eventId === c.eventId));
    },
    formattedGameHighlights: (state) => {
      const hasAccess = state.data?.isEventAvailableForUser ?? false;
      return state.gameHighlights.map((highlight) => {
        const isPremium = highlight.commerceType === "premium" && !hasAccess;
        return {
          id: highlight.id,
          imageUrl: highlight.imageUrl,
          title: highlight.title,
          duration: undefined,
          type: "game" as const,
          isPremium,
          commerceType: highlight.commerceType,
        };
      });
    },
    formattedPlayerHighlights: (state) => {
      return state.playerHighlights
        .filter(highlight => !!highlight.playerNumber)
        .map((highlight) => {
          const hasAccess = highlight.isEventAvailableForUser ?? false;
          const isPremium = highlight.commerceType === "premium" && !hasAccess;
          return {
            id: highlight.id,
            imageUrl: highlight.imageUrl,
            playerNumber: highlight.playerNumber,
            playerColor: highlight.playerColor,
            commerceType: highlight.commerceType,
            isPremium,
            locked: isPremium,
          };
        });
    },
  },

  actions: {
    async load(id: string, type?: TEventType) {
      const userStore = useUserStore();
      const appConfig = useRuntimeConfig().public;
      const isEnableMatchStats = appConfig.APP_CUSTOMER.enable_match_stats;
      const isGameHighlightEnabled = appConfig.APP_CUSTOMER.enable_game_highlights;
      const isPlayerHighlightsEnabled = appConfig.APP_CUSTOMER.enable_player_highlights;
      const isEnabledEventScoring = appConfig.APP_CUSTOMER.enable_event_scoring;

      this.loading = true;

      this.data = await this.loadEvent(id, type, isEnabledEventScoring);
      if (!this.data) {
        showError({ statusCode: 404, data: { context: EVENT_TYPE.EVENT } });
        return;
      }

      const eventId = this.data.eventId;
      const { commerceCoverage } = this.data;
      const isCommerce = (
        commerceCoverage.includes(COMMERCE_CATEGORY.PPV)
        || commerceCoverage.includes(COMMERCE_CATEGORY.SUBSCRIPTION)
      );
      const isStatsAvailableForUser = eventId
        && isEnableMatchStats
        && this.data.isEventAvailableForUser
        && this.data.isEventAvailableInUserRegion
        && this.data.isEnabledVidswapStats;

      if (this.data.eventStatus === EVENT_STATUS.CHANNEL) {
        const loadCommerceOptionsPromise = isCommerce ? this.loadCommerceOptions(this.data) : Promise.resolve([]);
        const loadChannelProgramPromise = this.loadChannelProgram(this.data);
        const [commerceOptions, channelProgram] = await Promise.all([
          loadCommerceOptionsPromise,
          loadChannelProgramPromise,
        ]);
        this.commerceOptions = commerceOptions;
        this.channelProgram = channelProgram;

        this.canReloadSuggested = false;
        this.loading = false;
        return;
      }

      const loadClipsPromise = eventId && userStore.isAuthenticated ? this.loadUserMedia(eventId, EVENT_TYPE.USER_CLIP) : Promise.resolve([]);
      const loadUserHighlightsPromise = eventId
        && userStore.isAuthenticated
        && isGameHighlightEnabled
        ? this.loadUserMedia(eventId, EVENT_TYPE.USER_HIGHLIGHT)
        : Promise.resolve([]);
      const loadStatsPromise = isStatsAvailableForUser ? this.loadStats(eventId) : Promise.resolve({ scores: null, overview: null, rosterOverview: null });
      const loadGameHighlightsPromise = eventId && isGameHighlightEnabled ? this.loadHighlights(eventId) : Promise.resolve([]);
      const loadPlayerHighlightsPromise = eventId && isPlayerHighlightsEnabled ? this.loadPlayerHighlights(eventId) : Promise.resolve([]);
      const loadCommerceOptionsPromise = isCommerce ? this.loadCommerceOptions(this.data) : Promise.resolve([]);

      const [userClips, userHighlights, gameHighlights, playerHighlights, commerceOptions, stats] = await Promise.all([
        loadClipsPromise,
        loadUserHighlightsPromise,
        loadGameHighlightsPromise,
        loadPlayerHighlightsPromise,
        loadCommerceOptionsPromise,
        loadStatsPromise,
      ]);

      this.userClips = userClips;
      this.userHighlights = userHighlights;
      this.gameHighlights = gameHighlights;
      this.playerHighlights = playerHighlights;
      this.commerceOptions = commerceOptions;
      this.scoreStats = stats.scores;
      this.gameStats = stats.overview;
      this.athleteStats = stats.rosterOverview;

      this.canReloadSuggested = false;
      this.loading = false;
    },
    async loadEvent(id: string, type?: TEventType, extendWithScoreStats?: boolean) {
      const segment = useSegment();

      let event = null;
      try {
        event = await EventsAPI.loadById({ id, type, extendWithScoreStats });

        if (event && event.eventType === "highlight") {
          segment.page("Highlight", {
            event_id: event.eventId,
            highlight_id: event.id,
            previous_path: window.history.state?.back,
          });
        }

        if (event && event.eventType === "user_clip") {
          segment.page("Clip", {
            event_id: event.eventId,
            clip_id: event.id,
            previous_path: window.history.state?.back,
          });
        }
      }
      catch (err) {
        console.error(err);
      }
      return event;
    },
    loadUserMedia(
      eventId: string,
      user_media_type: TUserMediaType,
    ): Promise<IModelMediaEvent[]> {
      return EventsAPI.userMedia
        .load({
          page: 0,
          size: 24,
          order: ORDER.desc,
          sort: SORT.added,
          event_id: [eventId],
          media_type: [user_media_type],
          publish_status: [EVENT_MEDIA_PUBLISH_STATUS.NONE, EVENT_MEDIA_PUBLISH_STATUS.PROCESSED],
        })
        .then(response => response.content.entries)
        .catch((error) => {
          console.error(`Failed to load User ${user_media_type}`, error);
          return [];
        });
    },
    loadHighlights(eventId: string): Promise<IModelMediaEvent[]> {
      return EventsAPI.userMedia
        .loadHighlights({ page: 0, size: 24, order: ORDER.desc, event_id: [eventId] })
        .then(response => response.content.entries)
        .catch((error) => {
          console.error("Failed to load Game Highlights", error);
          return [];
        });
    },
    loadPlayerHighlights(eventId: string): Promise<IModelMediaEvent[]> {
      return EventsAPI.userMedia
        .loadHighlights({ page: 0, size: 24, order: ORDER.desc, event_id: [eventId], type: [EVENT_MEDIA_TYPE.PLAYER] })
        .then(response => response.content.entries)
        .catch((error) => {
          console.error("Failed to load Player Highlights", error);
          return [];
        });
    },
    loadStats(eventId: string): Promise<IEventStats> {
      return EventsAPI.stats
        .getEventScoreStatsById(eventId)
        .catch((error) => {
          console.error("Failed to load Score Stats", error);
          return { scores: null, overview: null, rosterOverview: null };
        });
    },
    loadCommerceOptions(event: IModelEvent) {
      const id = (event.eventType === EVENT_TYPE.EVENT
        || event.eventType === EVENT_TYPE.CLIP
        || event.eventStatus === EVENT_STATUS.CHANNEL)
        ? event.id
        : event.eventId;

      if (!id)
        return [];

      return SubscriptionAPI
        .loadVodCommerceOptions(id)
        .catch((error) => {
          console.error("Failed to load commerce Options", error);
          return [];
        });
    },
    loadChannelProgram(event: IModelEvent) {
      if (!event.source.epg)
        return {} as IModelLinearProgram;

      return EventsAPI.loadChannelProgram(event.source.epg).catch((error) => {
        console.error("Failed to load channel program", error);
        return {} as IModelLinearProgram;
      });
    },
    reset(): void {
      this.data = null;
      this.loading = false;
      this.isEventStartingSoon = false;
      this.clips.isCreatingClip = false;
      this.clips.isEditingClip = false;
      this.channelProgram = {} as IModelLinearProgram;
      this.userClips = [];
      this.userHighlights = [];
      this.gameHighlights = [];
      this.playerHighlights = [];
      this.commerceOptions = [];
      this.scoreStats = null;
      this.gameStats = null;
      this.athleteStats = null;
    },
  },
});

export default useEventStore;

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useEventStore, import.meta.hot));
